import HTMLReactParser from "html-react-parser";

import { environmentURL } from "@src/constants";
import { PanMacmillanContributor } from "./models/panmacmillan";

const codeMacmillanChildrensBooks = "PC"
const nameMacmillanChildrensBooks = "Macmillan Children&#39;s Books"
const nameMacmillanChildrensBooksClean = "Macmillan Children's Books"
const codeBello = "PU"
const nameBello = "Bello"
const codeBluebird = "PZ"
const nameBluebird = "Bluebird"
const codeCampbell = "PA"
const nameCampbell = "Campbell Books"
const codeMacmillanCollectorsLibrary = "PV"
const nameMacmillanCollectorsLibrary ="Macmillan Collector&#39;s Library"
const nameMacmillanCollectorsLibraryClean ="Macmillan Collector's Library"
const codeMantle = "PJ"
const nameMantle = "Mantle"
const codePan = "PG"
const namePan = "Pan"
const codeMacmillan = "PL"
const codeMacmillanIN = "IM"
const nameMacmillan = "Macmillan"
const codeMacmillanPopularClassics = "IMPC"
const nameMacmillanPopularClassics = "Macmillan Popular Classics"
const codePanIndia = "IP"
const namePanIndia = "PAN India"
const codePicador = "PP"
const namePicador = "Picador"
const codePicadorIndia = "IPI"
const namePicadorIndia = "Picador India"
const codeTor = "TO"
const nameTor = "Tor"
const codeTwoHoots = "PE"
const nameTwoHoots = "Two Hoots"
const codeKingfisher = "PH"
const nameKingfisher = "Kingfisher"
const codeMacmillanDigitalAudio = "PD"
const nameMacmillanDigitalAudio = "Macmillan Digital Audio"
const codeOneBoat = "P3"
const nameOneBoat = "One Boat"
const codePicadorAfrica = "PASA"
const namePicadorAfrica = "Picador Africa"
const codePanMacmillanSouthAfrica = "PMSA";
const namePanMacmillanSouthAfrica = "Pan Macmillan South Africa";
const codePanMacmillanSouthAfricaChildrensBook = "PMSACB"
const namePanMacmillanSouthAfricaChildrensBook = "Pan Macmillan South Africa Children's Books"
const nameHarrimanHouse = "Harriman House"
const codeHarrimanHouse = "HH"

const authorCornerCodename = "author_corner"
const authorCornerSlug = "author-corner"
const childrenCodename = "books_for_children"
const childrenSlug = "books-for-children"
const classicsCodename = "classics"
const classicsSlug = "classics"
const crimeThrillerCodename = "crime___thrillers"
const crimeThrillerSlug = "crime-thriller" 
const fictionCodename = "fiction"
const fictionSlug = "fiction"
const generalCodename = "general"
const generalSlug = "general"
const historyCodename = "history"
const historySlug = "history" 
const lifeStyleCodename = "lifestyle___wellbeing"
const lifeStyleSlug = "lifestyle-wellbeing"
const literaryCodename = "literary"
const literarySlug = "literary"
const newsCodename = "news"
const newsSlug = "news"
const picadorCodename = "picador"
const picadorSlug = "picador"
const recipesCodename = "recipes"
const recipesSlug = "recipes"
const scienceFictionFantasyCodename = "science_fiction___fantasy"
const scienceFictionFantasySlug = "science-fiction-and-fantasy"
const tradeNewsCodename = "trade_news"
const tradeNewsSlug = "trade-news"
const youngAdultCodename = "young_adult"
const youngAdultSlug = "young-adult"

/**
 * Alphabet as array for looping through
*/
export function alphabetArray() {
 return "abcdefghijklmnopqrstuvwxyz".split(""); 
}

/**
 * Cleans a string for use in a URL.
 *
 * @param value The link's URL.
 * @returns The link's target.
 */
export function cleanStringForPath(path?: string, logging = false): string {
    if (!path || typeof path !== 'string' || !path.trim()) {
        if (logging) {
            console.warn(`[helpers.ts] cleanStringForPath: Invalid path:`, path);
        }
        return '';
    }

    const cleanedString = path
        .toLowerCase()
        .replace(/\s+/g, '-')         // Replace spaces with -
        .replace(/[&]/g, 'and')       // Replace & with 'and'
        .replace(/[^\w-]+/g, '')      // Remove non-word chars
        .replace(/--+/g, '-')         // Replace multiple - with single -
        .replace(/^-+/, '')           // Trim - from start
        .replace(/-+$/, '');          // Trim - from end

    if (logging) {
        console.log(`[helpers.ts] cleanStringForPath: Original path:`, path, `| Cleaned:`, cleanedString);
    }

    return cleanedString;
}

/**
 * Cleans a string for use in a URL.
 *
 * @param value The link's URL.
 * @returns filesize as a string.
 */
export function fileSizeAsString(fileSize:number) {
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    let fileSizeString;
    
    if (fileSize === 0) {
      fileSizeString = "0 Byte";
    } else {
      var i = parseInt(Math.floor(Math.log(fileSize) / Math.log(1024)));
      fileSizeString = Math.round(fileSize / Math.pow(1024, i), 2) + ' ' + sizes[i];
    }

    return fileSizeString
}

/**
 * Returns all contributors which match main roles
 *
 * @returns contributor array
 */
 export function filterByMainContributorRoles(contributors) {
  return contributors && contributors.filter(contributor => ["A01", "A02","A12","A13"].includes(contributor.contributorRole))
}


/**
 * Returns targetHeight for a jacket image based on the source width and height
 *
 * @returns number value for height of the jacket
 */
 export function getJacketHeight(sourceWidth:number, sourceHeight:number, targetWidth:number) {
  return sourceWidth && sourceHeight ? Math.round(targetWidth * (sourceHeight/sourceWidth)) : null
}

/**
 * Returns the number of pages in a result set
 *
 * @returns number of pages
 */
export function getPagingRange(from:number, to:number, step = 1) {
  let i = from;
  const range = [];

  while (i <= to) {
    range.push(i);
    i += step;
  }

  return range;
}

/**
 * Determines whether or not the current environment is a development environment.
 *
 * @returns *true* or *false*. Will default to *false* if no environment variable is found.
 */
export function isDevelopmentEnvironment(): boolean {
  const check: boolean = process.env.NODE_ENV as any;

  return check || false;
}

/**
 * Determines whether or not a given value is of the type *string*.
 *
 * @param value The value to check.
 * @returns *true* or *false*.
 */
export function isString(value: any): boolean {
  return typeof value === "string";
}

/**
 * Determines whether or not a given value is of the type *number*.
 *
 * @param value The value to check.
 * @returns *true* or *false*.
 */
export function isNumber(value: any): boolean {
  return typeof value === "number";
}

/**
 * Determines whether or not a given value is empty.
 *
 * @param value The value to check.
 * @returns *true* or *false*.
 */
export function isEmpty(value: any): boolean {
  return value === "" || value === null || value.length === 0 || typeof value === "undefined";
}

/**
 * Determines if a publication date is in the future
 *
 * @param publicationDate The date to check.
 * @returns *true* or *false*.
 */
 export function isPreOrder(publicationDate: Date): boolean {
  return publicationDate.getTime() > new Date().getTime()
}

/**
 * Determines whether to display item for preferred display
 *
 * @param array The haystack
 * @param value The string to match
 * @returns *true* or *false*.
 */
export function preferredDisplayContains(array:any, value:any) {
  return (array && array.length > 0 && array.some((e:any) => e.codename === value)) || value === "title" || value === "contributors"
}

/**
 * Strips markup of all HTML.
 *
 * @param markup HTML markup.
 * @returns The content stripped of HTML.
 */
export function stripHTML(markup: any): boolean {
  return markup.replace(/(<([^>]+)>)/gi, "").replace("&nbsp;", " ");
}

/**
 * Returns a JSON object as a renderable block of JSX code.
 *
 * @param object The JSON object.
 * @returns The JSX code block.
 */
export function renderCodeBlock(object: Object) {
  return HTMLReactParser(`<pre>${JSON.stringify(object, null, 2)}</pre>`);
}

/**
 * Transforms an internal absolute URL into a relative URL.
 *
 * @param value The absolute URL.
 * @returns The relative URL.
 */
export function resolveInternalURL(value: string) {
  let url: string = value;
  url = value.startsWith(environmentURL) ? (url = value.replace(environmentURL, "")) : url;

  return url;
}

/**
 * Determines whether or not a link target should be internal or external.
 *
 * @param value The link's URL.
 * @returns The link's target.
 */
export function resolveLinkTarget(value: string) {
  let target: "_blank" | "_self" = "_blank";
  if (value.startsWith(environmentURL) || value.startsWith("/")) target = "_self";

  return target;
}

/**
 * Truncates a string to the nearest full word and optionally appends a suffix
 *
 * @param str the original string
 * @param maxChars the max number of characters
 * @param suffix optional override for suffix
 * @returns The link's target.
 */
export function truncateString (str:string, maxChars=100, suffix = " . . ." ) {
  return str.length < maxChars ? str : `${str.substr(0, str.substr(0, maxChars - suffix.length).lastIndexOf(' '))}${suffix}`;
}

function FilterEditionNodesbyIsbnArray(editionNodes, isbnArray) {
  return editionNodes && editionNodes.filter(edition => (isbnArray.includes(edition.isbn) || isbnArray.includes(edition.node && edition.node.isbn)))
}

function FilterEditionNodesByFormat(editionNodes, formatArray) {
  return editionNodes && editionNodes.filter(thisEdition => {
    const edition = thisEdition.node ? thisEdition.node : thisEdition

    if(edition && edition.isPrimaryEdition && edition.isSoonPublished) {
      return formatArray.includes(edition.format)
    } else {
      return null
    }
  })
}

function FilterEditionNodesBySubject(editionNodes, subjectArray, onlyPrimaryEdition) {
    
    return editionNodes && editionNodes.filter(thisEdition => {
      const edition = thisEdition.node ? thisEdition.node : thisEdition
  
      if(edition && edition.isSoonPublished && edition.subject) {
        if(onlyPrimaryEdition === true && edition.isPrimaryEdition || onlyPrimaryEdition === false) {
           return edition.subject.some(subject => subjectArray.includes(subject.subjectCode))
        }
      } else {
        return null
      }
    })
  }
  

function FilterEditionNodesByImprint(editionNodes, imprintArray, onlyPrimaryEdition) {
  return editionNodes && editionNodes.filter(thisEdition => {
    const edition = thisEdition.node ? thisEdition.node : thisEdition

    if(edition && edition.isSoonPublished) {
      if(onlyPrimaryEdition === true && edition.isPrimaryEdition || onlyPrimaryEdition === false) {
        return imprintArray.some(r=> {
          const imprintName = r.name ? r.name : r
          return edition.imprintName === ResolveImprintName(imprintName)
        })
      }
    } else {
      return null
    }
  })
}

function FilterEditionNodesbyAgeRange(editionNodes, ageToFilterArray) {
    return editionNodes.filter(thisEdition => {
      const edition = thisEdition.node ? thisEdition.node : thisEdition
      return FilterEditionWithinAgeRange(edition, ageToFilterArray);
    })
  }
  function FilterEditionWithinAgeRange(edition, ageToFilterArray) {
    return ageToFilterArray && ageToFilterArray.length >= 1 && edition.audienceRange && edition.audienceRange[0] && edition.audienceRange[0].audienceRangeValueModified && edition.audienceRange[0].audienceRangeValueModified[0] && edition.audienceRange[0].audienceRangeValueModified[0] >= ageToFilterArray[0] && edition.audienceRange[0].audienceRangeValueModified[0] < ageToFilterArray[1]
  }

function FilterEditionsByCollectionIdentifier(editionNodes, collectionIdentifier) {
	if(!collectionIdentifier)
	{
		return editionNodes
	}
	else{
		return editionNodes && editionNodes.filter(thisEdition => {
			const edition = thisEdition.node ? thisEdition.node : thisEdition
			
			if(edition.isSoonPublished)
			{
				return (collectionIdentifier == "" && (!edition.collection || edition.collection.length == 0)) || (edition.collection && edition.collection.length > 0 && edition.collection[0].collectionIdentifier === collectionIdentifier)
			}
			else{
				return null
			}
		})
	}
    
}

function FilterEditionNodesbyContributorArray(editionNodes, contributorArray, contributorAndLogic) {
  return editionNodes.filter(edition => { 
    let editionHasContributor = false
    let thisEdition = edition.node ? edition.node : edition;
    
    if(contributorAndLogic === true) {
      if(thisEdition.contributors) {

        let editionContributorCount = 0;

        for (var i = 0; i < thisEdition.contributors.length; i++) {
          if(contributorArray.includes(thisEdition.contributors[i].contributorID)) {
            editionContributorCount++
          } 
        }

        editionHasContributor = contributorArray.length === editionContributorCount ?  true : false
      }
    } else {
      if(thisEdition.contributors) {
        
        for (var i = 0; i < thisEdition.contributors.length; i++) {
          if(contributorArray.includes(thisEdition.contributors[i].contributorID)) {
            editionHasContributor = true;
            break
          } 
        }
      }
    }

    return editionHasContributor;
  })
}

function FilterEditionsOnlyPrimaryEdition(editionNodes, onlyPrimaryEdition) {
  return editionNodes && editionNodes.filter(edition => {
    return onlyPrimaryEdition && onlyPrimaryEdition === true && edition.isPrimaryEdition || onlyPrimaryEdition !== undefined && onlyPrimaryEdition === false
  })
}

export function FilterEditionsByIsPublished(editionNodes, isPublished) {
	if(isPublished !== true && isPublished !== false)
	{
		return editionNodes
	}
	else{
		return editionNodes && editionNodes.filter(thisEdition => {
			const edition = thisEdition.node ? thisEdition.node : thisEdition
		
			return edition.isPublished === isPublished
		})
	}
  
}

export function filterEditions(editionNodes, isbnsCSV, contributorsCSV, formatsCSV, imprintArray, canDisplayAll = false, bisacCSV, themaCSV, bicCSV, ageToFilterArray,  onlyPrimaryEdition = false, isSoonPublished, contributorAndLogic = false, collectionIdentifier=null) {
  let filteredEditionNodes = editionNodes;

  filteredEditionNodes = FilterEditionsOnlyPrimaryEdition(editionNodes, onlyPrimaryEdition)
  
  if(isbnsCSV && isbnsCSV !== "") {
    const isbnArray =  isbnsCSV.replace(/\s/g, "").split(',').filter(function(e){return e}).map(String)
    filteredEditionNodes = FilterEditionNodesbyIsbnArray(filteredEditionNodes, isbnArray)

    return filteredEditionNodes
  } else {
    if(contributorsCSV && contributorsCSV !== "") {
      const contributorsArray = contributorsCSV.replace(/\s/g, "").split(',').filter(function(e){return e})
      filteredEditionNodes = FilterEditionNodesbyContributorArray(filteredEditionNodes, contributorsArray, contributorAndLogic)
    }
    
    if(formatsCSV && formatsCSV !== "") {
      const formatArray = formatsCSV.replace(/\s/g, "").split(',').filter(function(e){return e})
      filteredEditionNodes = FilterEditionNodesByFormat(filteredEditionNodes, formatArray)
    }

    if(bisacCSV && bisacCSV !== "" || themaCSV && themaCSV !== "" || bicCSV && bicCSV !== "") {
      const bisacArray = bisacCSV && bisacCSV !== "" ? bisacCSV.replace(/\s/g, "").split(',').filter(function(e){return e}) : []
      const themaArray = themaCSV && themaCSV !== "" ? themaCSV.replace(/\s/g, "").split(',').filter(function(e){return e}) : []
      const bicArray = bicCSV && bicCSV !== "" ? bicCSV.replace(/\s/g, "").split(',').filter(function(e){return e}) : []
      const prefixArray = (array, prefix) => array.map(item => `${prefix}-${item}`);

      const prefixedBicArray = prefixArray(bicArray, 'BIC');
      const prefixedBisacArray = prefixArray(bisacArray, 'BISAC');
      const prefixedThemaArray = prefixArray(themaArray, 'THEMA');

      const combinedSubjectsArray = prefixedBicArray.concat(prefixedBisacArray.concat(prefixedThemaArray));

      filteredEditionNodes = FilterEditionNodesBySubject(filteredEditionNodes, combinedSubjectsArray, onlyPrimaryEdition)
    }


    if(imprintArray && imprintArray.length > 0) {
      filteredEditionNodes = FilterEditionNodesByImprint(filteredEditionNodes, imprintArray, true)
    }


    if(ageToFilterArray && ageToFilterArray.length > 0) {
      filteredEditionNodes = FilterEditionNodesbyAgeRange(filteredEditionNodes, ageToFilterArray)
    }
	
	if(collectionIdentifier){
		filteredEditionNodes = FilterEditionsByCollectionIdentifier(filteredEditionNodes, collectionIdentifier)
	}

	

    return filteredEditionNodes && editionNodes && filteredEditionNodes.length !== editionNodes.length || canDisplayAll === true ? filteredEditionNodes : []
  }
}

export function FilterISBNsForModularContent(contentModularContent,panMacmillanEditionNodes) {
  let modularContentISBNs = [];

  contentModularContent && contentModularContent.filter(item => {
    if(item && item.system && item.system.type === "isbn_list_") 
    {
      const bookArray = item.elements.isbns && item.elements.isbns.value
      const contributorArray = item.elements.contributor_s_ && item.elements.contributor_s_.value
      const formatArray = item.elements.format_codes && item.elements.format_codes.value
      const imprintArray = item.elements.imprint.value && item.elements.imprint.value
      const tempFilteredEditions = FilterEditions(panMacmillanEditionNodes,bookArray,contributorArray,formatArray,imprintArray)

      tempFilteredEditions.forEach(edition => {
        modularContentISBNs.push(edition.isbn)
      })

    } else if (item && item.system && item.system.type === "book_spotlight" ) {
      modularContentISBNs.push(item.elements.isbn)
    } else if (item && item.system && item.system.type === "book_collection" ) {
      modularContentISBNs.concat(item.elements.isbn_list.value.replace(/\s/g, "").split(',').filter(function(e){return e}))
    } else if (item && item.system && item.system.type === "book_spotlight_collection") {
      const bookspotlights = item.elements && item.elements.book_spotlights && item.elements.book_spotlights.value

      bookspotlights && bookspotlights.forEach(spotlight => {
        spotlight && spotlight.elements && spotlight.elements.isbn && spotlight.elements.isbn.value && modularContentISBNs.push(spotlight.elements.isbn.value)
      })
    }
  })

return modularContentISBNs
}

export function FilterEditions(editionNodes, isbnsCSV, contributorsCSV, formatsCSV, imprintArray, canDisplayAll = false, bisacCSV, themaCSV, ageToFilterArray, primaryEdition, isSoonPublished, contributorAndLogic = false) {
  let filteredEditionNodes = editionNodes;
  
  if(isbnsCSV && isbnsCSV !== "") {
    const isbnArray =  isbnsCSV.replace(/\s/g, "").split(',').filter(function(e){return e}).map(String)
    filteredEditionNodes = FilterEditionNodesbyIsbnArray(filteredEditionNodes, isbnArray)
    return filteredEditionNodes
  } else {
    if(contributorsCSV && contributorsCSV !== "") {
      const contributorsArray = contributorsCSV.replace(/\s/g, "").split(',').filter(function(e){return e})
      filteredEditionNodes = FilterEditionNodesbyContributorArray(filteredEditionNodes, contributorsArray, contributorAndLogic)
    }
    
    if(formatsCSV && formatsCSV !== "") {
      const formatArray = formatsCSV.replace(/\s/g, "").split(',').filter(function(e){return e})
      filteredEditionNodes = FilterEditionNodesByFormat(filteredEditionNodes, formatArray)
    }

   if(bisacCSV && bisacCSV !== "" || themaCSV && themaCSV !== "") {
    const bisacArray = bisacCSV && bisacCSV !== "" ? bisacCSV.replace(/\s/g, "").split(',').filter(function(e){return e}) : []
    const themaArray = themaCSV && themaCSV !== "" ? themaCSV.replace(/\s/g, "").split(',').filter(function(e){return e}) : []
      const prefixArray = (array, prefix) => array.map(item => `${prefix}-${item}`);

      const prefixedBicArray = prefixArray(bicArray, 'BIC');
      const prefixedBisacArray = prefixArray(bisacArray, 'BISAC');
      const prefixedThemaArray = prefixArray(themaArray, 'THEMA');

      const combinedSubjectsArray = prefixedBicArray.concat(prefixedBisacArray.concat(prefixedThemaArray));

      filteredEditionNodes = FilterEditionNodesBySubject(filteredEditionNodes, combinedSubjectsArray, primaryEdition)
   }

    if(imprintArray && imprintArray.length > 0) {
      filteredEditionNodes = FilterEditionNodesByImprint(filteredEditionNodes, imprintArray)
    }

    if(ageToFilterArray && ageToFilterArray.length > 0) {
      filteredEditionNodes = FilterEditionNodesbyAgeRange(filteredEditionNodes, ageToFilterArray)
    }

    return filteredEditionNodes.length !== editionNodes.length || canDisplayAll === true ? filteredEditionNodes : []
  }
}

export function GetAgeRangeFromKontent(codename) {
  switch(codename) {
    case  "baby_and_toddler":
      return [0,3]
    case  "n0___5":
      return [0,6]      
    case "n3___5":
      return [3,6]
    case "n6___8":
      return [6,9]
    case "n6___9":
      return [6,10]
    case "n9___12":
      return [9,12]
    case "n10___12":
      return [10,13]
    case "n12___15":
      return [12,16]
    default:
     return [12, 9999] 
  }
}

export function ResolveImprintName(imprintCode) {
    switch(imprintCode) {
      case codeHarrimanHouse:
      case nameHarrimanHouse:
        return nameHarrimanHouse
      case codeBello:
      case nameBello:
        return nameBello
      case codeBluebird:
      case nameBluebird:
        return nameBluebird
      case codeCampbell:
      case nameCampbell:
        return nameCampbell
      case codeMacmillanChildrensBooks:
      case nameMacmillanChildrensBooks:
      case nameMacmillanChildrensBooksClean:
        return nameMacmillanChildrensBooksClean
      case codeMacmillanCollectorsLibrary:
      case nameMacmillanCollectorsLibrary:
      case nameMacmillanCollectorsLibraryClean:
        return nameMacmillanCollectorsLibraryClean
      case codeMantle:
      case nameMantle:
        return nameMantle
      case codePan:
      case namePan:
        return namePan
      case codeMacmillanIN:
      case codeMacmillan:
      case nameMacmillan:
        return nameMacmillan
      case codePicador:
      case namePicador:
        return namePicador
      case codeTor:
      case nameTor:
        return nameTor
      case codeTwoHoots:
      case nameTwoHoots:
        return nameTwoHoots    
      case codeKingfisher:
      case nameKingfisher:  
        return nameKingfisher   
      case codeMacmillanDigitalAudio:
      case nameMacmillanDigitalAudio:
        return nameMacmillanDigitalAudio
      case codeOneBoat:
      case nameOneBoat:
        return nameOneBoat
      case codePicadorAfrica:
      case namePicadorAfrica:
        return namePicadorAfrica
      case codePanMacmillanSouthAfrica:
      case namePanMacmillanSouthAfrica:
        return namePanMacmillanSouthAfrica
      case codePanMacmillanSouthAfricaChildrensBook:
      case namePanMacmillanSouthAfricaChildrensBook:
        return namePanMacmillanSouthAfricaChildrensBook
      case codePanIndia:
      case namePanIndia:
        return namePanIndia
      case codeMacmillanPopularClassics:
      case nameMacmillanPopularClassics:
        return nameMacmillanPopularClassics
      case codePicadorIndia:
      case namePicadorIndia:
        return namePicadorIndia
      default:
          return null
    }
  }  

export function ResolveImprintCode(imprintName) {
    switch(imprintName) {
      case "Harriman House":
      case codeHarrimanHouse:
          return codeHarrimanHouse
      case "Bello":
      case codeBello:
        return codeBello
      case "Bluebird":
      case codeBluebird:
        return codeBluebird
      case "Campbell":
      case "Campbell Books":
      case codeCampbell:
        return codeCampbell
      case "Macmillan Childrens Books":
      case "Macmillan Children's Books":
      case codeMacmillanChildrensBooks:
        return codeMacmillanChildrensBooks
      case "Macmillan Collectors Library":
      case "Macmillan Collector's Library":
      case codeMacmillanCollectorsLibrary:
        return codeMacmillanCollectorsLibrary
      case "Macmillan Popular Classics":
      case codeMacmillanPopularClassics:
        return codeMacmillanPopularClassics
      case "Mantle":
      case codeMantle:
        return codeMantle
      case "Pan":
      case codePan:
        return codePan
      case "PAN India":
        return codePanIndia
      case codeMacmillanIN:
        return codeMacmillanIN
      case "Macmillan":
      case codeMacmillan:
        return codeMacmillan
      case "Picador": 
      case codePicador:
        return codePicador
      case "Picador India":
      case codePicadorIndia:
        return codePicadorIndia
      case "Tor": 
      case codeTor:
        return codeTor
      case "Two Hoots": 
      case codeTwoHoots:
        return codeTwoHoots    
      case "Kingfisher":
      case codeKingfisher:
        return codeKingfisher   
      case "Macmillan Digital Audio":
      case codeMacmillanDigitalAudio:
        return codeMacmillanDigitalAudio
      case "One Boat":
      case codeOneBoat:
        return codeOneBoat
      case "Picador Africa":
      case codePicadorAfrica:
        return codePicadorAfrica
      case "Pan Macmillan South Africa":
      case codePanMacmillanSouthAfrica:
        return codePanMacmillanSouthAfrica
      default:
          return null
    }
  }

export function ResolveArticleCategoryToSlug(category_codename) {
  switch(category_codename) {
    case lifeStyleCodename:
      return lifeStyleSlug
    case youngAdultCodename:
      return youngAdultSlug
    case classicsCodename:
      return classicsSlug
    case generalCodename:
      return generalSlug
    case childrenCodename:
      return childrenSlug
    case fictionCodename:
      return fictionSlug
    case literaryCodename:
      return literarySlug
    case scienceFictionFantasyCodename:
      return scienceFictionFantasySlug
    case crimeThrillerCodename:
      return crimeThrillerSlug 
    case historyCodename:
      return historySlug
    case authorCornerCodename:
      return authorCornerSlug
    case newsCodename:
      return newsSlug
    case picadorCodename:
      return picadorSlug
    case recipesCodename:
      return recipesSlug
    case tradeNewsCodename:
      return tradeNewsSlug      
  }
}

export function ResolveFormat(format) {
  const formats = { 
    "AA":"Audio",
    "AB":"Audio Cassette",
    "AC":"CD Audio",
    "AJ":"Audiobook",
    "AN":"Audiobook",
    "AO":"Audiobook",
    "AZ":"Other audio format",
    "BA":"Book","BB":"Hardback",
    "BC":"Paperback",
    "BD":"Loose-leaf",
    "BH":"Board book",
    "BI":"Rag book",
    "BJ":"Bath book",
    "BK":"Novelty book",
    "BL":"Slide bound",
    "BM":"Big book",
    "BN":"Part-work (fascículo)",
    "BO":"Fold-out book or chart",
    "BP":"Foam book",
    "BZ":"Other book format",
    "DA":"Digital (on physical carrier)",
    "DI":"DVD-ROM",
    "EA":"Ebook",
    "EB":"Ebook",
    "EC":"Digital online",
    "ED":"Ebook",
    "PA":"Miscellaneous print",
    "PB":"Address book",
    "PC":"Calendar",
    "PD":"Cards",
    "PF":"Diary",
    "PJ":"Postcard book or pack",
    "PK":"Poster",
    "PL":"Record book",
    "PR":"Notebook / blank book",
    "PS":"Organizer",
    "PZ":"Other printed item",
    "SA":"Multiple-component retail product",
    "SB":"Box set",
    "SC":"Slip case",
    "SD":"Multiple-component retail product, shrink-wrapped",
    "SE":"Multiple-component retail product, loose",
    "SF":"Multiple-component retail product, part(s) enclosed",
    "ZA":"General merchandise",
    "ZB":"Doll",
    "ZC":"Soft toy",
    "ZD":"Toy",
    "ZE":"Game",
    "ZZ":"Other merchandise"
  }

  for (var key in formats) {
    if(key === format) {
      return formats[key]
    }
  }

  return "Format unknown"
}

export function ResolveFormatSchema(format) {
const formats = { 
  "AA":"https://schema.org/AudiobookFormat",
  "AB":"https://schema.org/AudiobookFormat",
  "AC":"https://schema.org/AudiobookFormat",
  "AJ":"https://schema.org/AudiobookFormat",
  "AN":"https://schema.org/AudiobookFormat",
  "AO":"https://schema.org/AudiobookFormat",
  "AZ":"https://schema.org/AudiobookFormat",
  "BA":"https://schema.org/bookFormat",
  "BB":"https://schema.org/Hardcover",
  "BC":"https://schema.org/Paperback",
  //"BD":"Loose-leaf",
  //"BH":"Board book",
  //"BI":"Rag book",
  //"BJ":"Bath book",
  //"BK":"Novelty book",
  //"BL":"Slide bound",
  //"BM":"Big book",
  //"BN":"Part-work (fascículo)",
  //"BO":"Fold-out book or chart",
  //"BP":"Foam book",
  //"BZ":"Other book format",
  "DA":"https://schema.org/EBook",
  //"DI":"DVD-ROM",
  "EA":"https://schema.org/EBook",
  "EB":"https://schema.org/EBook",
  "EC":"https://schema.org/EBook",
  "ED":"https://schema.org/EBook",
  //"PA":"Miscellaneous print",
  //"PB":"Address book",
  //"PC":"Calendar",
  //"PD":"Cards",
  //"PF":"Diary",
  //"PJ":"Postcard book or pack",
  //"PK":"Poster",
  //"PL":"Record book",
  //"PR":"Notebook / blank book",
  //"PS":"Organizer",
  //"PZ":"Other printed item",
  //"SA":"Multiple-component retail product",
  //"SB":"Multiple-component retail product, boxed",
  //"SC":"Multiple-component retail product, slip-cased",
  //"SD":"Multiple-component retail product, shrink-wrapped",
  //"SE":"Multiple-component retail product, loose",
  //"SF":"Multiple-component retail product, part(s) enclosed",
  //"ZA":"General merchandise",
  //"ZB":"Doll",
  //"ZC":"Soft toy",
  //"ZD":"Toy",
  //"ZE":"Game",
  //"ZZ":"Other merchandise"
}

for (var key in formats) {
  if(key === format) {
    return formats[key]
  } 
}

return ""
}